import React, { useState } from "react";
import { Modal, Form, Input, Button, Select, Alert, message } from "antd";
import api from "../../api/axiosConfig";

const { Option } = Select;

const UserAuthModal = ({ closeModal, setLoggedInUser }) => {
  const [step, setStep] = useState("phone"); // 'phone', 'otp', 'login', 'register', 'forgotPasswordPhone', 'forgotPasswordOtp'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginMethod, setLoginMethod] = useState("password"); // Default to password for existing users
  const [form] = Form.useForm();

  const handlePhoneSubmit = async (values) => {
    setLoading(true);
    setError("");
    form.resetFields();
    try {
      const checkUserResponse = await api.post("/user/check-user", {
        phoneNumber: values.phoneNumber,
      });
      setPhoneNumber(values.phoneNumber);
      setIsNewUser(checkUserResponse.data.isNewUser);

      if (checkUserResponse.data.isNewUser) {
        await api.post("/user/send-otp", {
          phoneNumber: values.phoneNumber,
        });
        setStep("otp");
        message.success("An OTP has been sent to your phone number.");
      } else {
        setLoginMethod("password");
        setStep("login");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "An error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLoginOtpRequest = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/user/send-otp", { phoneNumber });
      setLoginMethod("otp");
      message.success("An OTP has been sent to your phone number.");
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to send OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (values) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/user/login", {
        phoneNumber,
        password: values.password,
      });
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      setLoggedInUser(user);
      message.success("Login successful!");
      closeModal();
    } catch (err) {
      // Explicitly set the error and do not close the modal
      setError(
        err.response?.data?.error || "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (values) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/user/verify-otp", {
        phoneNumber,
        otp: values.otp,
      });
      if (isNewUser) {
        setStep("register");
        message.success(response.data.message);
      } else {
        // This path is for "Login with OTP"
        const { token, user } = response.data;
        localStorage.setItem("token", token);
        setLoggedInUser(user);
        message.success("Login successful!");
        closeModal();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (values) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/user/signup", {
        phoneNumber,
        ...values,
      });
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      setLoggedInUser(user);
      message.success("Registration successful!");
      closeModal();
    } catch (err) {
      setError(
        err.response?.data?.error || "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPasswordOtp = async () => {
    setLoading(true);
    setError("");
    try {
        await api.post('/user/forgot-password-otp', { phoneNumber });
        setStep('forgotPasswordOtp');
        message.success('An OTP has been sent to your phone number.');
    } catch (err) {
        setError(err.response?.data?.error || 'Failed to send OTP. Please check the phone number.');
    } finally {
        setLoading(false);
    }
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    setError('');
    try {
        await api.post('/user/reset-password-otp', {
            phoneNumber,
            otp: values.otp,
            newPassword: values.newPassword,
        });
        message.success('Password has been reset successfully. Please log in.');
        setStep('login'); // Go back to login screen
        form.resetFields();
    } catch (err) {
        setError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
        setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "phone":
        return (
          <Form form={form} onFinish={handlePhoneSubmit} layout="vertical">
            <h2>Login or Sign Up</h2>
            {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />}
            <Form.Item
              name="phoneNumber"
              rules={[{ required: true, message: "Please input your phone number!" }, { pattern: /^\d{10}$/, message: "Please enter a valid 10-digit phone number." }]}
            >
              <Input placeholder="Enter 10-digit mobile number" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Continue
              </Button>
            </Form.Item>
          </Form>
        );
      case "otp":
        return (
          <Form form={form} onFinish={handleOtpSubmit} layout="vertical">
            <h2>Verify OTP</h2>
            <p>An OTP has been sent to {phoneNumber}</p>
            {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />}
            <Form.Item name="otp" rules={[{ required: true, message: "Please input the OTP!" }]}>
              <Input placeholder="Enter OTP" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Verify & Sign Up
              </Button>
            </Form.Item>
          </Form>
        );
      case "login":
        return (
          <div>
            <h2>Login to Your Account</h2>
            <p>Welcome back! Your phone is {phoneNumber}.</p>
            {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />}
            {loginMethod === "password" ? (
              <Form form={form} onFinish={handlePasswordLogin} layout="vertical">
                <Form.Item name="password" rules={[{ required: true, message: "Please enter your password!" }]}>
                  <Input.Password placeholder="Password" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} block>
                    Login
                  </Button>
                </Form.Item>
                <div style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={handleLoginOtpRequest}>Or login with OTP</Button>
                    <br/>
                    <Button type="link" onClick={() => { setStep('forgotPasswordOtp'); handleForgotPasswordOtp(); }}>Forgot Password? Click here</Button>
                </div>
              </Form>
            ) : (
              <Form form={form} onFinish={handleOtpSubmit} layout="vertical">
                <Form.Item name="otp" rules={[{ required: true, message: "Please input the OTP!" }]}>
                  <Input placeholder="Enter OTP" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} block>
                    Login with OTP
                  </Button>
                </Form.Item>
                <Button type="link" onClick={() => setLoginMethod("password")}>Or login with password</Button>
              </Form>
            )}
          </div>
        );
      case "register":
        return (
          <Form form={form} onFinish={handleRegisterSubmit} layout="vertical">
            <h2>Create Your Account</h2>
            {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />}
            <Form.Item name="name" rules={[{ required: true, message: "Please input your name!" }]}>
              <Input placeholder="Name" />
            </Form.Item>
            <Form.Item name="age" rules={[{ required: true, message: "Please input your age!" }]}>
              <Input type="number" placeholder="Age" />
            </Form.Item>
            <Form.Item name="gender" rules={[{ required: true, message: "Please select your gender!" }]}>
              <Select placeholder="Select Gender">
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: "Please create a password!" }]}>
              <Input.Password placeholder="Password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Sign Up
              </Button>
            </Form.Item>
          </Form>
        );
    case 'forgotPasswordOtp':
        return (
            <Form form={form} onFinish={handleResetPassword} layout="vertical">
                <h2>Reset Your Password</h2>
                <p>An OTP has been sent to {phoneNumber}.</p>
                {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} />}
                <Form.Item name="otp" rules={[{ required: true, message: 'Please enter the OTP' }]}>
                    <Input placeholder="Enter OTP" />
                </Form.Item>
                <Form.Item name="newPassword" label="New Password" rules={[{ required: true, message: 'Please enter a new password' }]}>
                    <Input.Password />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block>Reset Password</Button>
                </Form.Item>
                <Button type="link" onClick={() => { setStep('phone'); setError(''); form.resetFields(); }}>Back to Login</Button>
            </Form>
        );
      default:
        return null;
    }
  };

  return (
    <Modal open={true} onCancel={closeModal} footer={null} destroyOnClose>
      {renderStep()}
    </Modal>
  );
};

export default UserAuthModal;